import { createPublicClient, defineChain, type PublicClient, fallback } from 'viem'
import {
  arbitrum,
  avalanche,
  avalancheFuji,
  bsc,
  mainnet,
  optimism,
  optimismSepolia,
  polygon,
  polygonMumbai,
  sepolia,
  base,
} from 'viem/chains'

import { createStorage, http } from '@wagmi/core'
import { getDefaultConfig } from '@rainbow-me/rainbowkit'
import { NetworkType } from 'abis/set/sets'
import {
  rainbowWallet,
  walletConnectWallet,
  coinbaseWallet,
  safeWallet,
  metaMaskWallet,
} from '@rainbow-me/rainbowkit/wallets'

export const viemChains = [
  mainnet,
  avalanche,
  arbitrum,
  avalancheFuji,
  polygonMumbai,
  sepolia,
  optimismSepolia,
  base,
  bsc,
]

export const networkToChain = (network: NetworkType) => {
  switch (network) {
    case NetworkType.Mainnet:
      return mainnet
    case NetworkType.Avalanche:
      return avalanche
    case NetworkType.Arbitrum:
      return arbitrum
    case NetworkType.Optimism:
      return optimism
    case NetworkType.Base:
      return base
    case NetworkType.Bsc:
      return bsc
    default:
      return mainnet
  }
}

export const optimismSepoliaStack = defineChain({
  // ...opChainConfig,
  name: 'OP Stack Example',
  id: optimismSepolia.id,
  nativeCurrency: {
    name: 'Sepolia Ether',
    symbol: 'ETH',
    decimals: 18,
  },
  rpcUrls: {
    default: {
      http: ['https://sepolia.optimism.io'],
    },
  },
})

export const optimismStack = defineChain({
  // ...opChainConfig,
  name: 'OP Mainnet',
  id: optimism.id,
  nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
  rpcUrls: {
    default: {
      http: ['https://optimism-rpc.publicnode.com'],
    },
  },
})

export const baseStack = defineChain({
  // ...baseChainConfig,
  name: 'Base',
  id: base.id,
  nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
  rpcUrls: {
    default: {
      http: ['https://mainnet.base.org'],
    },
  },
})

const storage = createStorage({
  key: 'wagmi-phuture-storage',
  storage: localStorage,
})
const apiKey = import.meta.env.VITE_APP_ALCHEMY_ID

export const wagmiConfig = getDefaultConfig({
  appName: 'Phuture Finance',
  storage,
  projectId: 'f2101fb0bc3b353011763befd80ef263',
  chains: [arbitrum, avalanche, mainnet, optimism, polygon, base, bsc],
  wallets: [
    {
      groupName: 'Recommended',
      wallets: [metaMaskWallet],
    },
    {
      groupName: 'Popular',
      wallets: [rainbowWallet, coinbaseWallet, walletConnectWallet, safeWallet],
    },
  ],
  transports: {
    [arbitrum.id]: fallback([
      http('https://arbitrum-one-rpc.publicnode.com'),
      http(`https://arb-mainnet.g.alchemy.com/v2/${apiKey}`),
    ]),
    [avalanche.id]: fallback([
      http(`https://avax-mainnet.g.alchemy.com/v2/${apiKey}`),
      http('https://avalanche.drpc.org'),
    ]),
    [mainnet.id]: fallback([
      http('https://eth.llamarpc.com'),
      http(`https://eth-mainnet.g.alchemy.com/v2/${apiKey}`),
    ]),
    [optimism.id]: fallback([
      http('https://optimism.llamarpc.com'),
      http(`https://opt-mainnet.g.alchemy.com/v2/${apiKey}`),
    ]),
    [polygon.id]: fallback([
      http('https://polygon-bor-rpc.publicnode.com'),
      http('https://polygon-rpc.com'),
    ]),
    [base.id]: fallback([
      http(`https://base-mainnet.g.alchemy.com/v2/${apiKey}`),
      http('https://base.drpc.org'),
    ]),
    [bsc.id]: fallback([
      http('https://bsc.drpc.org'),
      http(`https://bnb-mainnet.g.alchemy.com/v2/${apiKey}`),
    ]),
  },
})

export const viemPublicClients: { [key: number]: PublicClient } = {
  [arbitrum.id]: createPublicClient({
    chain: arbitrum,
    transport: fallback([
      http('https://arbitrum-one-rpc.publicnode.com'),
      http(`https://arb-mainnet.g.alchemy.com/v2/${apiKey}`),
    ]),
  }),
  [avalanche.id]: createPublicClient({
    chain: avalanche,
    transport: fallback([
      http(`https://avax-mainnet.g.alchemy.com/v2/${apiKey}`),
      http('https://avalanche.drpc.org'),
    ]),
  }),
  [bsc.id]: createPublicClient({
    chain: bsc,
    transport: fallback([
      http('https://bsc.drpc.org'),
      http(`https://bnb-mainnet.g.alchemy.com/v2/${apiKey}`),
    ]),
  }),
  [mainnet.id]: createPublicClient({
    chain: mainnet,
    transport: fallback([
      http('https://eth.llamarpc.com'),
      http(`https://eth-mainnet.g.alchemy.com/v2/${apiKey}`),
    ]),
  }),
  [base.id]: createPublicClient({
    chain: baseStack,
    transport: fallback([
      http(`https://base-mainnet.g.alchemy.com/v2/${apiKey}`),
      http('https://base.drpc.org'),
    ]),
  }),
  [optimism.id]: createPublicClient({
    chain: optimismStack,
    transport: fallback([
      http('https://optimism.llamarpc.com'),
      http(`https://opt-mainnet.g.alchemy.com/v2/${apiKey}`),
    ]),
  }),
  [polygon.id]: createPublicClient({
    chain: polygon,
    transport: fallback([
      http('https://polygon-bor-rpc.publicnode.com'),
      http('https://polygon-rpc.com'),
    ]),
  }),

  //TESTNETS
  [avalancheFuji.id]: createPublicClient({
    chain: avalancheFuji,
    transport: http('https://avalanche-fuji-c-chain.publicnode.com'),
  }),
  [sepolia.id]: createPublicClient({
    chain: sepolia,
    transport: http('https://ethereum-sepolia.publicnode.com'),
  }),
  [optimismSepolia.id]: createPublicClient({
    chain: optimismSepoliaStack,
    transport: http('https://sepolia.optimism.io'),
  }),
}
